import { useEffect, useState } from "react"
import { IPickList } from "../../../List/DisposalsPickList"
import { httpGet } from "../../../../../modules/Backend"
import SidePanelInfo from "../components/SidePanelInfo"
import { EntityNames } from "../../../../../enums/EntityNames"
import useDisposalPickListActions from "../../../picking/useDisposalPickListActions"
import DisposalPickingButtons from "../../../picking/DisposalPickingButtons"
import SidePanelDisposalYears from "../components/SidePanelDisposalYears"
import PrintDisposalPickList from "../../../print/PrintDisposalPickList"
import FileDownloadWidget from "../../../FileDownloadWidget"
import SidePanelList from "../components/SidePanelList"
import DeleteIcon from '../../../../../assets/icons/icon-cross-red.svg';
import Form from "../../../entityComponents/Form/Form"
import IconTickGreen from "../../../../../assets/icons/icon-green-tick-circle.svg"
import IconTickGrey from "../../../../../assets/icons/icon-grey-tick-circle.svg"
import IconExclamationRed from "../../../../../assets/icons/icon-exclamation-red.svg"
import ReactDOM from "react-dom"

export interface IDisposalCheck {
    id: string,
    operatorName: string,
}

enum DisposalPickListSidePanelMode {
    View = 0,
    AddDisposalCheck
}

export default function SidePanelDisposalPickList({ disposalId, items, minimumDisposalCheckCount, currentDisposalChecks, onDisposalCheckRemoved, onDisposalCheckAdded, maxBoxCountForOnDemandDisposal }: IPickList) {

    const [mode, setMode] = useState<DisposalPickListSidePanelMode>(DisposalPickListSidePanelMode.View);
    const [disposalCheck, setDisposalCheck] = useState<IDisposalCheck | undefined>(undefined);
    const [data, setData] = useState<any>();
    const { save, submit, cancel } = useDisposalPickListActions({
        disposalId,
        items,
        maxBoxCountForOnDemandDisposal
    });

    async function getData() {
        const result = await httpGet(`api/${EntityNames.disposals}/get?id=${disposalId}`)
        if (result.data) {
            setData(result.data);
        }
    }

    function switchToAddDisposalCheckView() {
        setMode(DisposalPickListSidePanelMode.AddDisposalCheck);
    }

    function handleDisposalCheckAdded(newDisposalCheck: IDisposalCheck) {
        setDisposalCheck(newDisposalCheck);
        setMode(DisposalPickListSidePanelMode.View);
        onDisposalCheckAdded(newDisposalCheck);
    }

    function handleDisposalCheckRemoved(removeFunc: ((item: IDisposalCheck) => void | undefined), itemToRemove: IDisposalCheck) {
        removeFunc(itemToRemove);
    }

    function onCancel() {
        setMode(DisposalPickListSidePanelMode.View);
    }

    function getDisposalCheckersHeaderWidget(listItems: IDisposalCheck[]) {
        return <div className="disposal-checks__list-header-widget">
            <img src={`${listItems.length >= minimumDisposalCheckCount ? IconTickGreen : IconTickGrey}`}></img>
            <p className="pick-list__check-count">{listItems?.length}/{minimumDisposalCheckCount}</p>
        </div>
    }

    function getInformationBarWidget() {
        return <div className="disposal-checks__list-information-bar">
            <img src={IconExclamationRed} />
            <p className="">Disposal must be inspected by {minimumDisposalCheckCount} different staff members.</p>
        </div>
    }

    function getDisposalChecksListWidget() {
        if (mode === DisposalPickListSidePanelMode.View) {
            return undefined;
        }

        return <div>
            <Form isCreate={true}
                entityName={EntityNames.disposalChecks}
                parentEntityName={EntityNames.disposals}
                parentId={disposalId}
                preLoadedParentData={data}
                isInline={true}
                disableAddEditEvents={true}
                onSubmitCallback={handleDisposalCheckAdded}
                onCancel={onCancel}
                formWrapperClassName='disposal-checks__list-form-wrapper'
                formActionsClassName='disposal-checks__list-form-actions'
                formButtonsClassName='disposal-checks__list-form-buttons'
                formButtonSize="small" />
        </div>
    }

    useEffect(() => {
        getData()
    }, [])

    return (
        <>
            <div className={`display--flex flex__direction--column height--full`}>
                <div className="display--flex alignItems--end gap--xs">
                    <h2>Disposal {data?.reference}</h2>
                </div>
                <div className="display--flex gap--xs flex__wrap--wrap margin__top--xs">
                    <FileDownloadWidget entityName={EntityNames.disposals} id={disposalId} onBeforeDownload={save} buttonText="Download Pick List" />
                    <PrintDisposalPickList disposalId={disposalId} disposalData={data} save={save} />
                </div>
                <SidePanelInfo
                    values={[
                        { label: "Account", value: data?.accountName },
                        { label: "Requested by", value: data?.requestedBy },
                        { label: "Requested at", value: data?.requestedAt },
                        { label: "Dispose material from year", value: data?.disposeMaterialOlderThanYears ? undefined : data?.disposeMaterialFromYear },
                        { label: "Dispose material older than", value: data?.disposeMaterialOlderThanYears ? `${data?.disposeMaterialOlderThanYears} years` : undefined },
                        { label: "Material types to dispose", value: data?.materialTypeName },
                    ]}
                />
                <SidePanelDisposalYears disposalId={data?.id} />

                <div className="padding__bottom--xs">
                    <SidePanelList<IDisposalCheck>
                        id={disposalId}
                        entityName={EntityNames.disposalChecks}
                        displayName="Disposal Checks"
                        headerWidget={getDisposalCheckersHeaderWidget}
                        listWidget={getDisposalChecksListWidget()}
                        informationBarWidget={getInformationBarWidget()}
                        searchLabel={undefined}
                        onAddNewClicked={switchToAddDisposalCheckView}
                        editFunction={undefined}
                        itemData={disposalCheck}
                        loadingRowCount={1}
                        searchFilter={undefined}
                        fixedHeight={currentDisposalChecks.length >= 3 ? 210 : 145}
                        isSmall={true}
                        prePopulatedItems={currentDisposalChecks}
                        onItemRemoved={onDisposalCheckRemoved}
                        canAdd={mode === DisposalPickListSidePanelMode.View}
                        renderRowContent={(item, edit, remove) => (
                            <>
                                <td>
                                    <div className="display--flex justifyContent--space-between padding--xxs alignItems--center">
                                        <div className="side-panel-table__cell-content">
                                            <p className="small small--2 textWrap">{item.operatorName}</p>
                                        </div>
                                        <div className="display--flex gap--m justifyContent--flex-end">
                                            {remove &&
                                                <div className="cursor__pointer side-panel-table__cell-content display--flex justifyContent--flex-end" onClick={() => handleDisposalCheckRemoved(remove, item)}>
                                                    <img className="small-1" src={DeleteIcon} />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </td>
                            </>
                        )}
                        displayColumns={
                            <>
                                <th><p className="small"></p></th>
                            </>
                        }
                    />
                </div>
                <DisposalPickingButtons
                    save={save}
                    submit={submit}
                    cancel={cancel}
                    lineItems={items}
                    minimumDisposalCheckCount={minimumDisposalCheckCount}
                    currentDisposalChecks={currentDisposalChecks}
                />
            </div>
        </>
    )
}

